import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronDown, Apple, Play, Sparkles } from 'lucide-react';
import { MaskedLine } from './Reveal';
import { PhoneMockup } from './PhoneMockup';
import { useLang } from '../i18n/LangContext';
import { APP_STORE_URL } from '../storeLinks';

const StoreBadge = ({ icon: Icon, label, soon, testid, href }) => {
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="inline-flex items-center gap-2.5 rounded-full border border-krug-gold/50 px-5 py-2.5 text-left hover:border-krug-goldbright hover:bg-krug-gold/10 transition-[border-color,background-color] duration-300"
      >
        <Icon size={18} className="text-krug-goldbright" />
        <span className="leading-tight">
          <span className="block text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
          <span className="block text-xs font-bold text-white">{soon}</span>
        </span>
      </a>
    );
  }
  return (
    <button
      type="button"
      data-testid={testid}
      aria-disabled="true"
      title="Uskoro dostupno"
      className="inline-flex cursor-not-allowed items-center gap-2.5 rounded-full border border-white/15 px-5 py-2.5 text-left opacity-70"
    >
      <Icon size={18} className="text-slate-300" />
      <span className="leading-tight">
        <span className="block text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        <span className="block text-xs font-bold text-slate-300">{soon}</span>
      </span>
    </button>
  );
};

const LAUNCH_TS = new Date('2026-09-15T12:00:00+02:00').getTime();

const Countdown = ({ t }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const rawDiff = LAUNCH_TS - now;
  if (rawDiff <= 0) {
    return (
      <div data-testid="launch-live-banner" className="inline-flex items-center gap-4 rounded-3xl border border-krug-gold/40 bg-gradient-to-r from-krug-gold/15 to-krug-teal/10 px-6 py-5">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-krug-gold text-krug-navy shrink-0">
          <Sparkles size={22} />
        </span>
        <span>
          <span className="block font-heading text-xl md:text-2xl font-extrabold text-white">{t.hero.launchedTitle}</span>
          <span className="block text-sm text-slate-300 mt-1">{t.hero.launchedSub}</span>
        </span>
      </div>
    );
  }
  const diff = rawDiff;
  const units = [
    { value: Math.floor(diff / 86400000), label: t.hero.cDays, testid: 'countdown-days' },
    { value: Math.floor((diff % 86400000) / 3600000), label: t.hero.cHours, testid: 'countdown-hours' },
    { value: Math.floor((diff % 3600000) / 60000), label: t.hero.cMins, testid: 'countdown-mins' },
    { value: Math.floor((diff % 60000) / 1000), label: t.hero.cSecs, testid: 'countdown-secs' },
  ];
  return (
    <div data-testid="launch-countdown">
      <p className="flex items-center gap-2.5 text-sm uppercase tracking-[0.2em] text-krug-goldbright font-bold mb-4">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-krug-gold opacity-60" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-krug-goldbright" />
        </span>
        {t.hero.countdownTitle} · 15.09.2026
      </p>
      <div className="flex gap-3 md:gap-4">
        {units.map((u) => (
          <div
            key={u.label}
            data-testid={u.testid}
            className="min-w-[72px] md:min-w-[88px] rounded-2xl border-2 border-krug-gold/50 bg-gradient-to-b from-krug-surface to-krug-deep px-4 py-4 text-center shadow-[0_12px_40px_-12px_rgba(245,158,11,0.45)]"
          >
            <span className="block font-heading text-3xl md:text-4xl font-extrabold text-krug-goldbright tabular-nums">
              {String(u.value).padStart(2, '0')}
            </span>
            <span className="block text-[11px] uppercase tracking-widest text-slate-300 mt-1.5 font-bold">{u.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Hero = () => {
  const { t } = useLang();
  const reduced = useReducedMotion();
  const { scrollY } = useScroll();
  const yCenter = useTransform(scrollY, [0, 700], [0, reduced ? 0 : 90]);
  const yLeft = useTransform(scrollY, [0, 700], [0, reduced ? 0 : -50]);
  const yRight = useTransform(scrollY, [0, 700], [0, reduced ? 0 : 140]);

  return (
    <section id="pocetna" data-testid="hero-section" className="relative overflow-hidden bg-krug-navy pt-[72px]">
      <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full bg-krug-teal/25 blur-[140px]" aria-hidden="true" />
      <div className="absolute top-1/3 -right-48 w-[620px] h-[620px] rounded-full bg-krug-gold/15 blur-[160px]" aria-hidden="true" />
      <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full border border-krug-teal/25" aria-hidden="true" />
      <div className="absolute top-24 right-[12%] w-40 h-40 rounded-full border border-krug-gold/30" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-24 md:pb-32 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-heading text-xs md:text-sm font-bold tracking-[0.35em] uppercase text-krug-goldbright mb-7"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 data-testid="hero-headline" className="font-heading font-extrabold tracking-tighter text-white text-[clamp(3rem,7.5vw,5.75rem)] leading-[0.98]">
            <MaskedLine delay={0.25}>{t.hero.titleA}</MaskedLine>
            <MaskedLine delay={0.4}>
              <span className="text-gradient-gold">{t.hero.titleB}</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-7 text-lg md:text-xl font-medium text-slate-200 max-w-xl"
          >
            {t.hero.sub}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.78 }}
            className="mt-4 text-sm md:text-base text-slate-400 max-w-xl"
          >
            {t.hero.supporting}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#waitlist"
              data-testid="hero-cta-join"
              className="group inline-flex items-center gap-2 rounded-full bg-krug-gold px-7 py-3.5 font-heading font-bold text-krug-navy hover:bg-krug-goldbright transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#funkcije"
              data-testid="hero-cta-showcase"
              className="inline-flex items-center rounded-full border border-white/20 px-7 py-3.5 font-heading font-bold text-white hover:border-krug-tealbright hover:text-krug-tealbright transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="mt-10"
          >
            <Countdown t={t} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-9"
          >
            <p data-testid="hero-store-note" className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-4">
              {t.hero.storeNote}
            </p>
            <div className="flex flex-wrap gap-3">
              <StoreBadge icon={Apple} label="App Store" soon={t.hero.storeLive} testid="hero-appstore-badge" href={APP_STORE_URL} />
              <StoreBadge icon={Play} label="Google Play" soon={t.hero.storeSoon} testid="hero-googleplay-badge" />
            </div>
          </motion.div>
        </div>

        <div data-testid="hero-phones" className="relative h-[560px] md:h-[640px] hidden sm:block">
          <motion.div style={{ y: yLeft }} className="absolute left-0 top-16 w-[210px] md:w-[230px] rotate-[-7deg] opacity-80">
            <div className="animate-float-soft" style={{ animationDelay: '1.2s' }}>
              <PhoneMockup src="/assets/screen-discover.webp" alt="KRUG aplikacija — ekran Istraži s biznisima iz dijaspore" glow="teal" />
            </div>
          </motion.div>
          <motion.div style={{ y: yRight }} className="absolute right-0 top-28 w-[210px] md:w-[230px] rotate-[7deg] opacity-80">
            <div className="animate-float-soft" style={{ animationDelay: '2.1s' }}>
              <PhoneMockup src="/assets/screen-home.webp" alt="KRUG aplikacija — početni ekran s događajima i restoranima" glow="none" />
            </div>
          </motion.div>
          <motion.div
            style={{ y: yCenter }}
            initial={{ opacity: 0, y: 60, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-1/2 -translate-x-1/2 top-0 w-[250px] md:w-[280px] z-10"
          >
            <div className="animate-float-soft">
              <PhoneMockup src="/assets/screen-welcome.webp" alt="KRUG aplikacija — dobrodošlica i registracija" glow="gold" />
            </div>
          </motion.div>
        </div>

        <div className="sm:hidden -mx-5 px-5">
          <div className="w-[230px] mx-auto">
            <PhoneMockup src="/assets/screen-welcome.webp" alt="KRUG aplikacija — dobrodošlica i registracija" glow="gold" />
          </div>
        </div>
      </div>

      <motion.a
        href="#vrijednosti"
        data-testid="hero-scroll-cue"
        aria-label="Skrolaj dolje"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:inline-flex text-slate-500 hover:text-krug-tealbright transition-colors duration-300"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.a>
    </section>
  );
};
